import axios, { AxiosError } from "axios";
import { sendFile, sendPatch } from "./utils";
import { ProjectId, ProjectKey, ProjectVersionId } from "./projects";
import { User, UserId } from "./users";
import { DualType } from "@/typeUtils";
import { OrganizationId } from "./organizations";
import { CustomDataValueItem } from "./customData";

export type TicketSearchRequest = {
    text: string | null;
    project: ProjectId | null;

    assignee: UserId | null;
    assigned: boolean | null;

    reporter: UserId | null;

    priorities: PriorityId[] | null;
    excludePriorities: boolean;

    statuses: TicketStatusId[] | null;
    excludeStatuses: boolean;

    types: TicketTypeId[] | null;
    excludeTypes: boolean;

    labels: LabelId[] | null;
    excludeLabels: boolean;

    resolutions: ResolutionId[] | null;
    resolved: boolean | null;

    affectVersions: ProjectVersionId[] | null;
    hasAffectVersion: boolean | null;

    fixVersions: ProjectVersionId[] | null;
    hasFixVersion: boolean | null;

    lists: TicketListId[] | null;
    hasList: boolean | null;

    dueDate: Date | null;

    order: TicketSearchOrder | null;
    direction: TicketSearchDirection | null;
};

export enum TicketSearchOrder {
    Created = 0,
    Title = 1,
    Priority = 2,
    DueDate = 3,
    Status = 4,
    Type = 5,
    Assignee = 6,
    Reporter = 7,
    Resolved = 8,
    FixVersion = 9,
    AffectVersion = 10,
    List = 11,
}

export enum TicketSearchDirection {
    Ascending = 0,
    Descending = 1,
}

export type TicketSearchResult = {
    id: TicketId;
    key: TicketKey;
    title: string;
    projectId: ProjectId;
    priorityId: PriorityId;
    ticketTypeId: TicketTypeId;
    ticketStatusId: TicketStatusId;
    assignedToId: UserId | null;
    reportedById: UserId;
    resolved: boolean;
    origin: TicketOrigin;
    /** Set for portal tickets, whose reporters are not in the internal user list. */
    portalRequesterName: string | null;
    dueDate: Date | null;
    fixVersions: string[];
    affectVersions: string[];
    lists: string[];
    /** The text search matched a comment rather than the ticket itself. Only the search sets this. */
    matchedInComment?: boolean;
};

export type SearchResult = {
    tickets: TicketSearchResult[];
    total: number;
};

export type TicketId = string & { __brand: "TicketId" };
export type TicketKey = `${ProjectKey}-${number}` & { __brand: "TicketKey" };
export type Ticket = {
    id: TicketId;
    key: TicketKey;
    number: number;
    title: string;
    description: string | null;
    pendingDescriptionImport: boolean;
    pendingStatusMigration: boolean;
    created: Date;
    dueDate?: Date | null;
    resolved: Date | null;
    priorityId: PriorityId;
    resolutionId: ResolutionId | null;
    ticketStatusId: TicketStatusId;
    ticketTypeId: TicketTypeId;
    projectId: ProjectId;
    assignedToId: UserId | null;
    reportedById: UserId;
    parentId: TicketId | null;
    affectVersions: ProjectVersionId[];
    fixVersions: ProjectVersionId[];
    labels: LabelId[];
    origin: TicketOrigin;
    companyId: string | null;
    companyName: string | null;
    portalStage: PortalStage | null;
    portalRequester: { name: string; email: string } | null;
    /** The CC list of a support request, in the order people were added. */
    portalParticipants: PortalTicketParticipant[] | null;
    mergedInto: MergedIntoRequest | null;
    sla: TicketSla | null;
    /** The answered CSAT survey, when the requester filled one in. */
    survey: TicketSurvey | null;
    /** Other unresolved portal requests from the same company. */
    companyOpenRequests: number;
    customerNotificationsSilenced: boolean;
    /** Agent notes that have not been retracted, for the overflow menu badge. */
    agentNoteCount: number;
    /** An agent stopped and left a hand-off nobody has picked up yet. */
    agentHandoffPending: boolean;
};

export type TicketSurvey = {
    score: number;
    comment: string | null;
    respondedAt: string;
};

export type PortalTicketParticipant = {
    userId: UserId;
    name: string;
    email: string;
};

/** All durations are business minutes, measured against the project's support calendar. */
export type TicketSla = {
    firstResponseTargetMinutes: number | null;
    /** Null while the request is still unanswered. */
    firstResponseElapsedMinutes: number | null;
    firstResponseAt: string | null;
    firstResponseBreached: boolean;
    /** Time waited so far while still unanswered; null once answered or closed. */
    firstResponseWaitingMinutes: number | null;
    firstResponseDueAt: string | null;
    resolutionTargetMinutes: number | null;
    resolutionElapsedMinutes: number;
    resolvedAt: string | null;
    resolutionBreached: boolean;
    resolutionPaused: boolean;
    resolutionDueAt: string | null;
};

export enum TicketOrigin {
    Internal = 0,
    Portal = 1,
}

export { AgentNoteKind, PortalStage } from "./generated/enums";
import { AgentNoteKind, ChangeLogSource, PortalStage } from "./generated/enums";

export type TicketEdit = Pick<
    Ticket,
    | "title"
    | "description"
    | "priorityId"
    | "ticketStatusId"
    | "ticketTypeId"
    | "reportedById"
    | "parentId"
    | "assignedToId"
>;

export type TicketCreate = {
    title: string;
    type: TicketTypeId;
    priority: PriorityId;
    status: TicketStatusId;
    reporter: UserId;
    parent?: TicketId | null;
    assignedTo?: UserId | null;
    description?: string | null;
    /** "YYYY-MM-DD", same wire format as changeDueDate. */
    dueDate?: string | null;
    fixVersions?: ProjectVersionId[];
    affectVersions?: ProjectVersionId[];
    labels?: LabelId[];
    customData?: CustomDataValueItem[];
    draftAttachments?: AttachmentId[];
};

// Bulk edit: a present field is applied to every ticket; an absent field is left
// unchanged. A `value` of null clears a nullable field (unassign, remove due date).
export type BulkFieldChange<T> = { value: T };
export type BulkCollectionMode = "add" | "remove" | "replace";
export type BulkCollectionChange = { mode: BulkCollectionMode; value: string[] };

export type BulkTicketEdit = {
    ticketIds: TicketId[];
    assignee?: BulkFieldChange<UserId | null>;
    reporter?: BulkFieldChange<UserId>;
    priority?: BulkFieldChange<PriorityId>;
    status?: BulkFieldChange<TicketStatusId>;
    type?: BulkFieldChange<TicketTypeId>;
    dueDate?: BulkFieldChange<string | null>;
    fixVersions?: BulkCollectionChange;
    affectVersions?: BulkCollectionChange;
    labels?: BulkCollectionChange;
};

export type BulkTicketResolve = {
    ticketIds: TicketId[];
    ticketStatusId: TicketStatusId;
    resolutionId: ResolutionId | null;
};

export type BulkTicketResult = {
    ticketId: TicketId;
    success: boolean;
    error: string | null;
};

export type MergedIntoRequest = {
    id: TicketId;
    number: number;
    title: string;
    projectKey: string;
};

export type MergeCandidate = {
    id: TicketId;
    number: number;
    title: string;
    companyName: string | null;
    portalStage: PortalStage;
};

type TicketResponse = Omit<Ticket, "created" | "resolved"> & {
    created: string;
    resolved: string | null;
};

export type AttachmentId = string & { __brand: "AttachmentId" };
export type Attachment = {
    id: AttachmentId;
    created: Date;
    uploaderId: UserId;
    fileName: string;
    fileSize: number;
    mimeType: string;
    isPublic: boolean;
    url: string;
    thumbnailUrl: string | null;
};

export type EmailInnerAttachment = {
    index: number;
    name: string;
    size: number | null;
    mimeType: string | null;
};

/** A parsed .eml/.msg attachment; the HTML body arrives already sanitized by the server. */
export type EmailAttachment = {
    subject: string;
    from: string | null;
    to: string[];
    cc: string[];
    date: string | null;
    textBody: string | null;
    htmlBody: string | null;
    hasBlockedImages: boolean;
    attachments: EmailInnerAttachment[];
};

type AttachmentResponse = Omit<Attachment, "created"> & {
    created: string;
};

export type TicketWorkLogId = string & { __brand: "TicketWorkLogId" };
export type TicketWorkLog = {
    id: TicketWorkLogId;
    userId: UserId;
    started: Date;
    minutesSpent: number;
    text: string | null;
    /** How many other tickets share the entry this came out of. */
    sharedWith: number;
    entryMinutes: number;
};

export type TicketWorkLogs = {
    entries: TicketWorkLog[];
    totalMinutes: number;
};

type TicketWorkLogResponse = Omit<TicketWorkLog, "started"> & {
    started: string;
};

export type TicketCommentId = string & { __brand: "TicketCommentId" };
export type TicketComment = {
    id: TicketCommentId;
    userId: UserId;
    created: Date;
    text: string;
    isPublic: boolean;
    /** Posted through the inbound email pipeline rather than a signed-in session. */
    viaEmail: boolean;
    /** Set only for portal authors, who are absent from the internal user list. */
    authorName: string | null;
    lastEdit: TicketCommentEdit | null;
};

export type TicketAgentNoteId = string & { __brand: "TicketAgentNoteId" };
/** An agent's scratchpad entry, written through the MCP server and hidden from the ticket's normal views. */
export type TicketAgentNote = {
    id: TicketAgentNoteId;
    userId: UserId;
    created: Date;
    text: string;
    kind: AgentNoteKind;
    model: string | null;
    sessionId: string | null;
    agentId: string | null;
    replyToId: TicketAgentNoteId | null;
    retractedAt: Date | null;
};

type TicketAgentNoteResponse = Omit<TicketAgentNote, "created" | "retractedAt"> & {
    created: string;
    retractedAt: string | null;
};

export type TicketCommentEditId = string & { __brand: "TicketCommentEditId" };
export type TicketCommentEdit = {
    id: TicketCommentEditId;
    date: Date;
    userId: UserId;
};

export type TicketCommentEditDetail = TicketCommentEdit & {
    oldText: string;
};

type TicketCommentEditResponse = Omit<TicketCommentEdit, "date"> & {
    date: string;
};

type TicketCommentEditDetailResponse = Omit<TicketCommentEditDetail, "date"> & {
    date: string;
};

type TicketCommentResponse = Omit<TicketComment, "created" | "edits"> & {
    created: string;
    edits: TicketCommentEditResponse[];
};

export type TicketHistoryItem = {
    field: string;
    from: string | null;
    to: string | null;
};

export type TicketHistoryEntryId = string & { __brand: "TicketHistoryEntryId" };
export type TicketHistoryEntry = {
    id: TicketHistoryEntryId;
    date: Date;
    userId: UserId;
    /** Workflow entries were written by routing on the user's behalf after their status change. */
    source: ChangeLogSource;
    items: TicketHistoryItem[];
};

export type TicketHistory = {
    total: number;
    entries: TicketHistoryEntry[];
};

type TicketHistoryEntryResponse = Omit<TicketHistoryEntry, "date"> & {
    date: string;
};

type TicketHistoryResponse = Omit<TicketHistory, "entries"> & {
    entries: TicketHistoryEntryResponse[];
};

export type TicketResponsibilityUser = {
    userId: UserId;
    count: number;
    first: Date;
    last: Date;
};

export type TicketResponsibilityRole = {
    roleId: TicketResponsibilityRoleId;
    name: string;
    users: TicketResponsibilityUser[];
};

type TicketResponsibilityUserResponse = Omit<TicketResponsibilityUser, "first" | "last"> & {
    first: string;
    last: string;
};

type TicketResponsibilityRoleResponse = Omit<TicketResponsibilityRole, "users"> & {
    users: TicketResponsibilityUserResponse[];
};

/** One status move that earned a credit. */
export type TicketResponsibilityMove = {
    date: Date;
    userId: UserId;
    roleId: TicketResponsibilityRoleId;
    roleName: string;
    fromStatusId: TicketStatusId;
    toStatusId: TicketStatusId;
};

export type TicketResponsibilities = {
    roles: TicketResponsibilityRole[];
    /** Oldest first. */
    flow: TicketResponsibilityMove[];
};

type TicketResponsibilitiesResponse = {
    roles: TicketResponsibilityRoleResponse[];
    flow: (Omit<TicketResponsibilityMove, "date"> & { date: string })[];
};

export type TicketLinks = {
    incoming: Map<TicketLinkTypeId, TicketSearchResult[]>;
    outgoing: Map<TicketLinkTypeId, TicketSearchResult[]>;
    bidirectional: Map<TicketLinkTypeId, TicketSearchResult[]>;
};

export type TicketLinkTypeId = string & { __brand: "TicketLinkTypeId" };
export type TicketLinkType = {
    id: TicketLinkTypeId;
    name: string;
    bidirectional: boolean;
    ticketLabel: string;
    relatedLabel: string;
};

type TicketLinkResponse = {
    incoming: Array<{
        linkTypeId: TicketLinkTypeId;
        tickets: TicketSearchResult[];
    }>;
    outgoing: Array<{
        linkTypeId: TicketLinkTypeId;
        tickets: TicketSearchResult[];
    }>;
    bidirectional: Array<{
        linkTypeId: TicketLinkTypeId;
        tickets: TicketSearchResult[];
    }>;
};

export type LabelId = string & { __brand: "LabelId" };
export type Label = {
    id: LabelId;
    usedBy: number;
    name: string;
};

export type PriorityId = string & { __brand: "PriorityId" };
export type Priority = DualType<
    {
        id: PriorityId;
        usedBy: number;
        name: string;
        order: number;
        description: string | null;
    },
    { icon: string; iconColor: string }
>;

export type ResolutionId = string & { __brand: "ResolutionId" };
export type Resolution = {
    id: ResolutionId;
    usedBy: number;
    name: string;
    description: string | null;
};

export enum TicketStatusCategory {
    ToDo = 0,
    InProgress = 1,
    Done = 2,
}

export type TicketStatusId = string & { __brand: "TicketStatusId" };
export type TicketStatus = {
    id: TicketStatusId;
    usedBy: number;
    name: string;
    category: TicketStatusCategory;
    order: number;
    isEntry: boolean;
    color: string | null;
    description: string | null;
    portalStage: PortalStage;
    /** Reporting stage; null keeps the status out of every stage breakdown. */
    stageId: TicketStatusStageId | null;
    pendingDeletion: boolean;
};

export type TicketStatusStageId = string & { __brand: "TicketStatusStageId" };

export type TicketStatusSetId = string & { __brand: "TicketStatusSetId" };

export type TicketResponsibilityRoleId = string & { __brand: "TicketResponsibilityRoleId" };

export type TicketTypeId = string & { __brand: "TicketTypeId" };
export type TicketType = DualType<
    {
        id: TicketTypeId;
        usedBy: number;
        name: string;
        description: string | null;
        subtaskOnly: boolean;
        statusSetIds: TicketStatusSetId[];
    },
    {
        icon: string;
        iconColor: string;
    }
>;

export type TicketWebLinkId = string & { __brand: "TicketWebLinkId" };
export type TicketWebLink = {
    id: TicketWebLinkId;
    name: string | null;
    url: string;
};

export enum TicketListUserPermissionLevels {
    Unauthorized = -1,
    Unknown = 0,

    CanView = 10,

    CanEdit = 20,

    Creator = 100,
}

export type TicketListChangeLogItem = {
    ticketId?: TicketId;
    field: "add" | "remove" | "note" | "purpose" | "description";
    from?: string;
    to?: string;
};

export type TicketListChangeLog = {
    user: UserId;
    date: Date;
    changes: TicketListChangeLogItem[];
};

export type TicketListId = string & {
    __brand: "TicketListId";
};

export type TicketListTicketModel = {
    title: string;
    key: string;
    priority: Pick<Priority, "icon" | "iconColor" | "name" | "order">;
    assignee: Pick<User, "displayName" | "userName">;
    description: string;
    status: Pick<TicketStatus, "name" | "category" | "color">;
};

export type TicketListTicket = {
    ticketId: TicketId;
    note?: string;
    order?: number;
    ticket?: TicketListTicketModel;
};

export type TicketList = {
    id: TicketListId;
    name: string;
    purpose: string | null;
    description: string | null;
    tickets: TicketListTicket[];
    projectId?: ProjectId | undefined;
    organizationId?: OrganizationId | undefined;
    creator: UserId;
    created: Date;
    changeLogs: TicketListChangeLog[];
};

export type TicketListStub = Pick<TicketList, "id" | "name" | "projectId" | "organizationId"> & {
    count: number;
    permission?: TicketListUserPermissionLevels | null;
    ticketIds?: TicketId[] | undefined;
};

export type TicketListUpdateModel = {
    listId: TicketListId;
    add?: TicketListTicket[] | undefined;
    remove?: TicketId[] | undefined;
    name?: string | undefined;
    purpose?: string | undefined;
    description?: string | undefined;
    order?:
        | {
              ticketId: TicketId;
              order: number;
          }[]
        | undefined;
};

export type TicketListUserPermission = {
    userId: UserId;
    permission: TicketListUserPermissionLevels;
    implicit: boolean;
    reason: string | null;
};

export type TicketApi = {
    search(
        request: TicketSearchRequest,
        page: number | null,
        perPage: number | null
    ): Promise<SearchResult>;
    searchDeleted(
        request: TicketSearchRequest,
        page: number | null,
        perPage: number | null
    ): Promise<SearchResult>;
    get(id: TicketId | TicketKey): Promise<Ticket>;
    getByKey(key: string): Promise<Ticket | null>;

    create(project: ProjectId, ticket: TicketCreate): Promise<Ticket>;

    edit(id: TicketId, edit: TicketEdit): Promise<void>;
    patch(ticket: Ticket, patch: Partial<TicketEdit>): Promise<void>;
    resolve(id: TicketId, status: TicketStatusId, resolution: ResolutionId | null): Promise<void>;
    delete(id: TicketId, deleteChildren: boolean): Promise<void>;
    restore(ids: TicketId[]): Promise<void>;
    bulkEdit(model: BulkTicketEdit): Promise<BulkTicketResult[]>;
    bulkResolve(model: BulkTicketResolve): Promise<BulkTicketResult[]>;

    getDueDate(id: TicketId): Promise<Date | null>;
    changeDueDate(id: TicketId, dueDate: string | null): Promise<void>;

    getChildren(id: TicketId): Promise<TicketSearchResult[]>;

    getAttachments(id: TicketId): Promise<Attachment[]>;
    uploadAttachment(id: TicketId, file: File): Promise<Attachment>;
    deleteAttachment(ticket: TicketId, attachment: AttachmentId): Promise<void>;
    setAttachmentVisibility(
        ticket: TicketId,
        attachment: AttachmentId,
        isPublic: boolean
    ): Promise<void>;
    uploadDraftAttachment(project: ProjectId, file: File): Promise<Attachment>;
    deleteDraftAttachment(project: ProjectId, attachment: AttachmentId): Promise<void>;
    getAttachmentEmail(
        ticket: TicketId,
        attachment: AttachmentId,
        images: boolean
    ): Promise<EmailAttachment>;
    attachmentEmailPartUrl(ticket: TicketId, attachment: AttachmentId, part: number): string;

    getWorkLogs(id: TicketId): Promise<TicketWorkLogs>;
    addWorkLog(
        id: TicketId,
        minutesSpent: number,
        started: Date | null,
        text: string | null
    ): Promise<TicketWorkLog>;
    deleteWorkLog(ticket: TicketId, workLog: TicketWorkLogId): Promise<void>;

    getComments(id: TicketId): Promise<TicketComment[]>;
    postComment(id: TicketId, text: string, isPublic?: boolean): Promise<TicketComment>;
    getHistory(ticket: TicketId, page: number, perPage: number): Promise<TicketHistory>;
    getResponsibilities(ticket: TicketId): Promise<TicketResponsibilities>;
    removeResponsibility(
        ticket: TicketId,
        role: TicketResponsibilityRoleId,
        user: UserId
    ): Promise<void>;
    getCommentEdits(ticket: TicketId, comment: TicketCommentId): Promise<TicketCommentEditDetail[]>;
    editComment(
        ticket: TicketId,
        comment: TicketCommentId,
        text: string
    ): Promise<TicketCommentEdit>;
    deleteComment(ticket: TicketId, comment: TicketCommentId): Promise<void>;
    setCommentVisibility(
        ticket: TicketId,
        comment: TicketCommentId,
        isPublic: boolean
    ): Promise<void>;

    getAgentNotes(id: TicketId): Promise<TicketAgentNote[]>;
    postAgentNote(id: TicketId, text: string, kind: AgentNoteKind): Promise<TicketAgentNote>;
    retractAgentNote(ticket: TicketId, note: TicketAgentNoteId): Promise<void>;
    deleteAgentNote(ticket: TicketId, note: TicketAgentNoteId): Promise<void>;

    getLinks(id: TicketId): Promise<TicketLinks>;
    broadcastToLinkedRequests(
        id: TicketId,
        text: string,
        resolve: boolean
    ): Promise<BulkTicketResult[]>;
    getMergeCandidates(id: TicketId): Promise<MergeCandidate[]>;
    mergeRequest(id: TicketId, survivor: TicketId): Promise<void>;
    addLink(id: TicketId, type: TicketLinkTypeId, linked: TicketId): Promise<void>;
    removeLink(id: TicketId, type: TicketLinkTypeId, linked: TicketId): Promise<void>;

    getWebLinks(id: TicketId): Promise<TicketWebLink[]>;
    createWebLink(ticketId: TicketId, url: string, name?: string): Promise<TicketWebLink>;
    deleteWebLink(ticketId: TicketId, webLinkId: TicketWebLinkId): Promise<void>;

    getAffectVersions(id: TicketId): Promise<ProjectVersionId[]>;
    setAffectVersions(id: TicketId, versions: ProjectVersionId[]): Promise<void>;

    getFixVersions(id: TicketId): Promise<ProjectVersionId[]>;
    setFixVersions(id: TicketId, versions: ProjectVersionId[]): Promise<void>;

    getLabels(): Promise<Label[]>;
    setLabels(id: TicketId, labels: LabelId[]): Promise<void>;
    addParticipant(id: TicketId, email: string): Promise<void>;
    removeParticipant(id: TicketId, user: UserId): Promise<void>;
    setCustomerNotificationsSilenced(id: TicketId, silenced: boolean): Promise<void>;
    moveTicket(id: TicketId, projectId: ProjectId): Promise<{ id: TicketId; key: TicketKey }>;

    getLinkTypes(): Promise<TicketLinkType[]>;
    getPriorities(): Promise<Priority[]>;
    getResolutions(): Promise<Resolution[]>;
    getStatuses(): Promise<TicketStatus[]>;
    getTypes(): Promise<TicketType[]>;

    getList(listId: TicketListId): Promise<TicketList>;
    getLists(opts?: {
        organizationId?: OrganizationId;
        projectId?: ProjectId;
    }): Promise<TicketListStub[]>;
    createList(
        list: Omit<TicketList, "id" | "creator" | "created" | "changeLogs">
    ): Promise<TicketList>;
    updateList(opts: TicketListUpdateModel): Promise<TicketList>;
    deleteList(listId: TicketListId): Promise<boolean>;
    getListPermissions(listId: TicketListId): Promise<TicketListUserPermission[]>;
    setListPermission(
        listId: TicketListId,
        userId: UserId,
        permission: TicketListUserPermissionLevels
    ): Promise<TicketListUserPermission[]>;
    removeListPermission(listId: TicketListId, userId: UserId): Promise<TicketListUserPermission[]>;
};

function formatTicket(ticket: TicketResponse): Ticket {
    return {
        ...ticket,
        created: new Date(ticket.created),
        resolved: ticket.resolved ? new Date(ticket.resolved) : null,
    };
}

function formatAttachment(attachment: AttachmentResponse): Attachment {
    return {
        ...attachment,
        created: new Date(attachment.created),
    };
}

function formatAgentNote(note: TicketAgentNoteResponse): TicketAgentNote {
    return {
        ...note,
        created: new Date(note.created),
        retractedAt: note.retractedAt ? new Date(note.retractedAt) : null,
    };
}

function formatComment(comment: TicketCommentResponse): TicketComment {
    return {
        ...comment,
        created: new Date(comment.created),
        lastEdit: comment.lastEdit
            ? { ...comment.lastEdit, date: new Date(comment.lastEdit.date) }
            : null,
    };
}

export const ticketApi: TicketApi = {
    async search(request, page, perPage) {
        const params = new URLSearchParams();
        if (page != null) params.set("page", page.toString());
        if (perPage != null) params.set("perPage", perPage.toString());

        const { data: result } = await axios.post<SearchResult>(
            `/api/v0/tickets/search?${params}`,
            request
        );

        return result;
    },
    async searchDeleted(request, page, perPage) {
        const params = new URLSearchParams();
        if (page != null) params.set("page", page.toString());
        if (perPage != null) params.set("perPage", perPage.toString());

        const { data: result } = await axios.post<SearchResult>(
            `/api/v0/tickets/deleted?${params}`,
            request
        );

        return result;
    },
    async get(id) {
        const { data: ticket } = await axios.get<TicketResponse>(`/api/v0/tickets/${id}`);
        return formatTicket(ticket);
    },
    async getByKey(key) {
        try {
            const { data: ticket } = await axios.get<TicketResponse>(`/api/v0/tickets/${key}`);
            return formatTicket(ticket);
        } catch (ex) {
            if (ex instanceof AxiosError && ex.response?.status === 404) {
                return null;
            } else {
                throw ex;
            }
        }
    },

    async create(project, ticket) {
        const { data: id } = await axios.post<TicketId>(
            `/api/v0/projects/${project}/tickets`,
            ticket
        );

        return await this.get(id);
    },

    async edit(ticket, edit) {
        await axios.post(`/api/v0/tickets/${ticket}`, edit);
    },
    async patch(ticket, patch) {
        const edited = {
            ...ticket,
            ...patch,
        };

        const { compare } = await import("fast-json-patch");

        const data = compare(ticket, edited);
        await sendPatch(`/api/v0/tickets/${ticket.id}`, data);
    },
    async resolve(id, ticketStatusId, resolutionId) {
        await axios.post(`/api/v0/tickets/${id}/resolution`, {
            ticketStatusId,
            resolutionId,
        });
    },

    async delete(id, deleteChildren) {
        await axios.delete(`/api/v0/tickets/${id}`, {
            params: { deleteChildren },
        });
    },

    async restore(ids) {
        await axios.post(`/api/v0/tickets/recover`, {
            ticketIds: ids,
        });
    },

    async bulkEdit(model) {
        const { data } = await axios.post<BulkTicketResult[]>(`/api/v0/tickets/bulk`, model);
        return data;
    },

    async bulkResolve(model) {
        const { data } = await axios.post<BulkTicketResult[]>(
            `/api/v0/tickets/bulk/resolution`,
            model
        );
        return data;
    },

    async getDueDate(id: TicketId): Promise<Date | null> {
        const { data: dueDate } = await axios.get<Date | null>(`/api/v0/tickets/${id}/dueDate`);
        return dueDate ? new Date(dueDate) : null;
    },

    async changeDueDate(id: string, dueDate: string | null): Promise<void> {
        try {
            await axios.put(`/api/v0/tickets/${id}/dueDate`, dueDate, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json, text/plain, */*",
                },
            });
        } catch (error) {
            console.error("Failed to update due date:", error);
            throw error;
        }
    },

    async getChildren(id) {
        const { data: children } = await axios.get<TicketSearchResult[]>(
            `/api/v0/tickets/${id}/children`
        );
        return children;
    },

    async getAttachments(id) {
        const { data: attachments } = await axios.get<AttachmentResponse[]>(
            `/api/v0/tickets/${id}/attachments`
        );

        return attachments.map(formatAttachment);
    },
    async uploadAttachment(id, file) {
        const attachment = await sendFile<AttachmentResponse>(
            `/api/v0/tickets/${id}/attachments`,
            file
        );

        return formatAttachment(attachment);
    },
    async deleteAttachment(ticket, attachment) {
        await axios.delete(`/api/v0/tickets/${ticket}/attachments/${attachment}`);
    },
    async setAttachmentVisibility(ticket, attachment, isPublic) {
        await axios.put(`/api/v0/tickets/${ticket}/attachments/${attachment}/visibility`, {
            isPublic,
        });
    },
    async uploadDraftAttachment(project, file) {
        const attachment = await sendFile<AttachmentResponse>(
            `/api/v0/projects/${project}/attachments`,
            file
        );

        return formatAttachment(attachment);
    },
    async deleteDraftAttachment(project, attachment) {
        await axios.delete(`/api/v0/projects/${project}/attachments/${attachment}`);
    },
    async getAttachmentEmail(ticket, attachment, images) {
        const { data } = await axios.get<EmailAttachment>(
            `/api/v0/tickets/${ticket}/attachments/${attachment}/email`,
            { params: { images: images || undefined } }
        );
        return data;
    },
    attachmentEmailPartUrl(ticket, attachment, part) {
        return `/api/v0/tickets/${ticket}/attachments/${attachment}/email/${part}`;
    },

    async getAffectVersions(id) {
        const { data: versions } = await axios.get<ProjectVersionId[]>(
            `/api/v0/tickets/${id}/versions/affect`
        );
        return versions;
    },
    async setAffectVersions(id, versions) {
        await axios.put(`/api/v0/tickets/${id}/versions/affect`, { versions });
    },

    async getFixVersions(id) {
        const { data: versions } = await axios.get<ProjectVersionId[]>(
            `/api/v0/tickets/${id}/versions/fix`
        );
        return versions;
    },
    async setFixVersions(id, versions) {
        await axios.put(`/api/v0/tickets/${id}/versions/fix`, { versions });
    },

    async getWorkLogs(id) {
        const { data } = await axios.get<{
            entries: TicketWorkLogResponse[];
            totalMinutes: number;
        }>(`/api/v0/tickets/${id}/workLogs`);
        return {
            totalMinutes: data.totalMinutes,
            entries: data.entries.map((w) => ({ ...w, started: new Date(w.started) })),
        };
    },
    async addWorkLog(id, minutesSpent, started, text) {
        const { data } = await axios.post<TicketWorkLogResponse>(`/api/v0/tickets/${id}/workLogs`, {
            minutesSpent,
            started,
            text,
        });
        return { ...data, started: new Date(data.started) };
    },
    async deleteWorkLog(ticket, workLog) {
        await axios.delete(`/api/v0/tickets/${ticket}/workLogs/${workLog}`);
    },

    async getComments(id) {
        const { data: comments } = await axios.get<TicketCommentResponse[]>(
            `/api/v0/tickets/${id}/comments`
        );

        return comments.map(formatComment);
    },
    async getAgentNotes(id) {
        const { data: notes } = await axios.get<TicketAgentNoteResponse[]>(
            `/api/v0/tickets/${id}/agent-notes`
        );
        return notes.map(formatAgentNote);
    },
    async postAgentNote(id, text, kind) {
        const { data: note } = await axios.post<TicketAgentNoteResponse>(
            `/api/v0/tickets/${id}/agent-notes`,
            { text, kind }
        );
        return formatAgentNote(note);
    },
    async retractAgentNote(ticket, note) {
        await axios.put(`/api/v0/tickets/${ticket}/agent-notes/${note}/retract`);
    },
    async deleteAgentNote(ticket, note) {
        await axios.delete(`/api/v0/tickets/${ticket}/agent-notes/${note}`);
    },
    async postComment(id, text, isPublic = false) {
        const { data: comment } = await axios.post<TicketCommentResponse>(
            `/api/v0/tickets/${id}/comments`,
            { text, isPublic }
        );

        return formatComment(comment);
    },
    async getHistory(ticket, page, perPage) {
        const { data } = await axios.get<TicketHistoryResponse>(
            `/api/v0/tickets/${ticket}/history`,
            { params: { page, perPage } }
        );

        return {
            total: data.total,
            entries: data.entries.map((e) => ({ ...e, date: new Date(e.date) })),
        };
    },
    async getResponsibilities(ticket) {
        const { data } = await axios.get<TicketResponsibilitiesResponse>(
            `/api/v0/tickets/${ticket}/responsibilities`
        );

        return {
            roles: data.roles.map((r) => ({
                ...r,
                users: r.users.map((u) => ({
                    ...u,
                    first: new Date(u.first),
                    last: new Date(u.last),
                })),
            })),
            flow: data.flow.map((m) => ({ ...m, date: new Date(m.date) })),
        };
    },
    async removeResponsibility(ticket, role, user) {
        await axios.delete(`/api/v0/tickets/${ticket}/responsibilities/${role}/users/${user}`);
    },
    async getCommentEdits(ticket, comment) {
        const { data: edits } = await axios.get<TicketCommentEditDetailResponse[]>(
            `/api/v0/tickets/${ticket}/comments/${comment}/edits`
        );

        return edits.map((e) => ({ ...e, date: new Date(e.date) }));
    },
    async editComment(ticket, comment, text) {
        const { data } = await axios.put<TicketCommentEditResponse>(
            `/api/v0/tickets/${ticket}/comments/${comment}`,
            { text }
        );
        return { ...data, date: new Date(data.date) };
    },
    async deleteComment(ticket, comment) {
        await axios.delete(`/api/v0/tickets/${ticket}/comments/${comment}`);
    },
    async setCommentVisibility(ticket, comment, isPublic) {
        await axios.put(`/api/v0/tickets/${ticket}/comments/${comment}/visibility`, { isPublic });
    },

    async getMergeCandidates(id) {
        const { data } = await axios.get<MergeCandidate[]>(
            `/api/v0/tickets/${id}/merge/candidates`
        );
        return data;
    },
    async mergeRequest(id, survivor) {
        await axios.post(`/api/v0/tickets/${id}/merge/${survivor}`);
    },

    async broadcastToLinkedRequests(id, text, resolve) {
        const { data } = await axios.post<BulkTicketResult[]>(
            `/api/v0/tickets/${id}/links/broadcast`,
            { text, resolve }
        );
        return data;
    },
    async getLinks(id) {
        const { data } = await axios.get<TicketLinkResponse>(`/api/v0/tickets/${id}/links`);
        return {
            incoming: new Map(data.incoming.map((l) => [l.linkTypeId, l.tickets])),
            outgoing: new Map(data.outgoing.map((l) => [l.linkTypeId, l.tickets])),
            bidirectional: new Map(data.bidirectional.map((l) => [l.linkTypeId, l.tickets])),
        };
    },
    async addLink(id, type, linked) {
        await axios.put(`/api/v0/tickets/${id}/links/${type}/${linked}`);
    },
    async removeLink(id, type, linked) {
        await axios.delete(`/api/v0/tickets/${id}/links/${type}/${linked}`);
    },

    async getWebLinks(id) {
        const { data: webLinks } = await axios.get<TicketWebLink[]>(
            `/api/v0/tickets/${id}/webLinks`
        );
        return webLinks;
    },
    async createWebLink(ticketId, url, name) {
        const { data: webLink } = await axios.post<TicketWebLink>(
            `/api/v0/tickets/${ticketId}/webLinks`,
            { url, name }
        );
        return webLink;
    },
    async deleteWebLink(ticketId, webLinkId) {
        await axios.delete(`/api/v0/tickets/${ticketId}/webLinks/${webLinkId}`);
    },

    async getLabels() {
        const { data: labels } = await axios.get<Label[]>("/api/v0/labels");
        return labels;
    },
    async setLabels(id, labels) {
        await axios.put(`/api/v0/tickets/${id}/labels`, { labels: labels });
    },
    async addParticipant(id, email) {
        await axios.post(`/api/v0/tickets/${id}/participants`, { email });
    },
    async moveTicket(id, projectId) {
        const { data } = await axios.post<{ id: TicketId; key: TicketKey }>(
            `/api/v0/tickets/${id}/move`,
            { projectId }
        );
        return data;
    },
    async removeParticipant(id, user) {
        await axios.delete(`/api/v0/tickets/${id}/participants/${user}`);
    },
    async setCustomerNotificationsSilenced(id, silenced) {
        await axios.put(`/api/v0/tickets/${id}/customer-notifications`, { silenced });
    },

    async getLinkTypes() {
        const { data: linkTypes } = await axios.get<TicketLinkType[]>("/api/v0/link-types");
        return linkTypes;
    },
    async getPriorities() {
        const { data: priorities } = await axios.get<Priority[]>("/api/v0/priorities");
        return priorities;
    },
    async getResolutions() {
        const { data: resolutions } = await axios.get<Resolution[]>("/api/v0/resolutions");
        return resolutions;
    },
    async getStatuses() {
        const { data: statuses } = await axios.get<TicketStatus[]>("/api/v0/statuses");
        return statuses;
    },
    async getTypes() {
        const { data: types } = await axios.get<TicketType[]>("/api/v0/types");
        return types;
    },
    async getList(listId) {
        const { data: result } = await axios.get<TicketList>(`/api/v0/tickets/lists/${listId}`);
        return result;
    },
    async getLists(opts) {
        if (opts == null) {
            const { data: lists } = await axios.get<TicketListStub[]>(`/api/v0/tickets/lists`);
            return lists;
        } else if (
            (opts.organizationId != null && opts.projectId != null) ||
            (opts.organizationId == null && opts.projectId == null)
        )
            throw new Error("getLists: Opts must include either organizationId OR projectId");
        else {
            const ownerType =
                opts.organizationId != null ? "org" : opts.projectId != null ? "proj" : null;
            const id = opts.organizationId ?? opts.projectId ?? null;
            const { data: lists } = await axios.get<TicketListStub[]>(
                `/api/v0/tickets/lists/${ownerType}/${id}`
            );

            return lists;
        }
    },
    async createList(list) {
        if (
            (list.organizationId != null && list.projectId != null) ||
            (list.organizationId == null && list.projectId == null)
        )
            throw new Error("createList: List must include either organizationId OR projectId");

        try {
            const { data: result } = await axios.post<TicketList>(`/api/v0/tickets/lists`, list);

            return result;
        } catch (e) {
            if (e instanceof AxiosError)
                throw new Error(
                    "createList: Either you do not have permission to create a list," +
                        " you do not have permission to view one of the tickets in this list," +
                        " or there was an internal server error that prevented it from being created."
                );
            else throw e;
        }
    },
    async updateList(opts) {
        const { data: result } = await axios.patch<TicketList>(
            `/api/v0/tickets/lists/${opts.listId}`,
            {
                add: opts.add,
                remove: opts.remove,
                name: opts.name,
                purpose: opts.purpose,
                description: opts.description,
                order: opts.order,
            }
        );
        return result;
    },
    async deleteList(listId) {
        const { data: result } = await axios.delete<boolean>(`/api/v0/tickets/lists/${listId}`);
        return result;
    },
    async getListPermissions(listId) {
        const { data: result } = await axios.get<TicketListUserPermission[]>(
            `/api/v0/tickets/lists/${listId}/permissions`
        );
        return result;
    },
    async setListPermission(listId, userId, permission) {
        const { data: result } = await axios.put<TicketListUserPermission[]>(
            `/api/v0/tickets/lists/${listId}/permissions/${userId}`,
            { permission }
        );
        return result;
    },
    async removeListPermission(listId, userId) {
        const { data: result } = await axios.delete<TicketListUserPermission[]>(
            `/api/v0/tickets/lists/${listId}/permissions/${userId}`
        );
        return result;
    },
};
